import React from 'react';
import { Modal, ModalBody, ModalFooter, Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCalendarAlt, 
    faClock, 
    faFileAlt, 
    faCheckCircle, 
    faTimesCircle, 
    faEdit,
    faSun,
    faThermometerHalf,
    faClipboardList,
    faFirstAid,
    faBaby,
    faHeart,
    faCalendarMinus,
    faPause,
    faGraduationCap,
    faUserMinus,
    faTimes
} from '@fortawesome/free-solid-svg-icons';
import { format, differenceInDays, differenceInHours } from 'date-fns';
import { it } from 'date-fns/locale';

const AbsenceDetailsModal = ({ isOpen, toggle, absence, onEdit }) => {
    if (!absence) return null;

    // Mappa delle icone per tipo di assenza
    const absenceIcons = {
        'ferie': faSun,
        'malattia': faThermometerHalf,
        'permesso': faClipboardList,
        'infortunio': faFirstAid,
        'maternità': faBaby,
        'paternità': faBaby,
        'lutto': faHeart,
        'congedo': faCalendarMinus,
        'aspettativa': faPause,
        'formazione': faGraduationCap,
        'default': faUserMinus
    };

    // Mappa dei colori per tipo di assenza
    const absenceColors = {
        'ferie': { bg: '#fef3c7', border: '#f59e0b', text: '#92400e', icon: '#f59e0b' },
        'malattia': { bg: '#fee2e2', border: '#ef4444', text: '#991b1b', icon: '#ef4444' },
        'permesso': { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af', icon: '#3b82f6' },
        'infortunio': { bg: '#fce7f3', border: '#ec4899', text: '#9d174d', icon: '#ec4899' },
        'maternità': { bg: '#fae8ff', border: '#d946ef', text: '#86198f', icon: '#d946ef' },
        'paternità': { bg: '#e0e7ff', border: '#6366f1', text: '#3730a3', icon: '#6366f1' },
        'lutto': { bg: '#f3f4f6', border: '#6b7280', text: '#374151', icon: '#6b7280' },
        'congedo': { bg: '#dcfce7', border: '#22c55e', text: '#166534', icon: '#22c55e' },
        'aspettativa': { bg: '#f5f5f4', border: '#78716c', text: '#44403c', icon: '#78716c' },
        'formazione': { bg: '#cffafe', border: '#06b6d4', text: '#155e75', icon: '#06b6d4' },
        'default': { bg: '#f3f4f6', border: '#9ca3af', text: '#374151', icon: '#9ca3af' }
    };

    // Formatta le date
    const formatDate = (dateString) => {
        try {
            const date = new Date(dateString);
            return format(date, "EEEE d MMMM yyyy", { locale: it });
        } catch (error) {
            return dateString;
        }
    };

    const formatTime = (dateString) => {
        try {
            const date = new Date(dateString);
            return format(date, 'HH:mm', { locale: it });
        } catch (error) {
            return '';
        }
    };

    const formatShortDate = (dateString) => {
        try {
            const date = new Date(dateString);
            return format(date, 'dd/MM/yyyy', { locale: it });
        } catch (error) {
            return dateString;
        }
    };

    // Calcola la durata
    const calculateDuration = () => {
        try {
            const start = new Date(absence.start);
            const end = new Date(absence.end);
            const days = differenceInDays(end, start);
            const hours = differenceInHours(end, start) % 24;
            
            if (days > 0) {
                return `${days} giorn${days === 1 ? 'o' : 'i'}${hours > 0 ? ` e ${hours} or${hours === 1 ? 'a' : 'e'}` : ''}`;
            }
            return `${hours} or${hours === 1 ? 'a' : 'e'}`;
        } catch (error) {
            return 'N/D';
        }
    };

    // Dati dell'assenza
    const absenceType = absence.absenceType || 'default';
    const absenceTypeLower = absenceType.toLowerCase();
    const absenceTypeFormatted = absenceType.charAt(0).toUpperCase() + absenceType.slice(1);
    const userLabel = absence.userLabel || 'Collaboratore';
    const userColor = absence.userColor || absence.backgroundColor || '#7367f0';
    const colors = absenceColors[absenceTypeLower] || absenceColors['default'];
    const icon = absenceIcons[absenceTypeLower] || absenceIcons['default'];

    // Gestione modifica
    const handleEdit = () => {
        toggle();
        if (onEdit) {
            onEdit(absence);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} centered size="md" className="absence-details-modal">
            <style>
                {`
                    .absence-details-modal .modal-content {
                        border: none;
                        border-radius: 16px;
                        overflow: hidden;
                        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                    }
                    .absence-details-modal .modal-header {
                        border-bottom: none;
                        padding: 0;
                        position: relative;
                    }
                    .absence-details-modal .modal-header .btn-close {
                        display: none;
                    }
                    .custom-close-btn {
                        position: absolute;
                        top: 16px;
                        right: 16px;
                        background: rgba(255,255,255,0.2);
                        border: none;
                        border-radius: 50%;
                        width: 32px;
                        height: 32px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        cursor: pointer;
                        transition: background 0.2s;
                        z-index: 10;
                    }
                    .custom-close-btn:hover {
                        background: rgba(255,255,255,0.3);
                    }
                    .absence-details-modal .modal-body {
                        padding: 0;
                    }
                    .absence-details-modal .modal-footer {
                        border-top: 1px solid #e5e7eb;
                        padding: 16px 24px;
                        background: #f9fafb;
                    }
                    .absence-type-badge {
                        display: inline-flex;
                        align-items: center;
                        gap: 8px;
                        padding: 8px 16px;
                        border-radius: 50px;
                        font-weight: 600;
                        font-size: 14px;
                    }
                    .detail-row {
                        display: flex;
                        align-items: flex-start;
                        padding: 12px 0;
                        border-bottom: 1px solid #f3f4f6;
                    }
                    .detail-row:last-child {
                        border-bottom: none;
                    }
                    .detail-icon {
                        width: 40px;
                        height: 40px;
                        border-radius: 10px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        margin-right: 16px;
                        flex-shrink: 0;
                    }
                    .detail-content {
                        flex: 1;
                    }
                    .detail-label {
                        font-size: 12px;
                        color: #6b7280;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        margin-bottom: 4px;
                    }
                    .detail-value {
                        font-size: 15px;
                        color: #111827;
                        font-weight: 500;
                    }
                    .user-avatar {
                        width: 48px;
                        height: 48px;
                        border-radius: 12px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 20px;
                        font-weight: 600;
                        color: white;
                        margin-right: 16px;
                    }
                    .status-badge {
                        display: inline-flex;
                        align-items: center;
                        gap: 6px;
                        padding: 6px 12px;
                        border-radius: 8px;
                        font-size: 13px;
                        font-weight: 500;
                    }
                    .status-approved {
                        background: #dcfce7;
                        color: #166534;
                    }
                    .status-pending {
                        background: #fef3c7;
                        color: #92400e;
                    }
                    .notes-box {
                        background: #f9fafb;
                        border-radius: 12px;
                        padding: 16px;
                        margin-top: 8px;
                    }
                `}
            </style>

            <div className="absence-modal-header" style={{
                background: `linear-gradient(135deg, ${userColor} 0%, ${userColor}dd 100%)`,
                padding: '24px',
                paddingRight: '60px',
                color: 'white',
                width: '100%',
                position: 'relative'
            }}>
                    {/* Pulsante chiusura custom */}
                    <button 
                        type="button" 
                        className="custom-close-btn" 
                        onClick={toggle}
                        aria-label="Chiudi"
                    >
                        <span style={{ color: 'white', fontSize: '18px', fontWeight: 300 }}>×</span>
                    </button>
                    
                    <div className="d-flex align-items-center mb-3">
                        <div className="user-avatar" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                            {userLabel.charAt(0).toUpperCase()}
                        </div>
                        <div>
                            <h4 className="mb-1" style={{ fontWeight: 600 }}>{userLabel}</h4>
                            <span style={{ opacity: 0.9, fontSize: '14px' }}>Dettagli Assenza</span>
                        </div>
                    </div>
                    
                    {/* Badge tipo assenza */}
                    <div 
                        className="absence-type-badge"
                        style={{
                            backgroundColor: colors.bg,
                            color: colors.text,
                            border: `2px solid ${colors.border}`
                        }}
                    >
                        <FontAwesomeIcon icon={icon} />
                        {absenceTypeFormatted}
                    </div>
            </div>

            <ModalBody>
                <div style={{ padding: '24px' }}>
                    {/* Periodo */}
                    <div className="detail-row">
                        <div className="detail-icon" style={{ backgroundColor: '#dbeafe' }}>
                            <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#3b82f6' }} />
                        </div>
                        <div className="detail-content">
                            <div className="detail-label">Periodo</div>
                            <div className="detail-value">
                                {formatShortDate(absence.start) === formatShortDate(absence.end) ? (
                                    formatDate(absence.start)
                                ) : (
                                    <>
                                        Dal <strong>{formatShortDate(absence.start)}</strong> al <strong>{formatShortDate(absence.end)}</strong>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Orario */}
                    <div className="detail-row">
                        <div className="detail-icon" style={{ backgroundColor: '#fef3c7' }}>
                            <FontAwesomeIcon icon={faClock} style={{ color: '#f59e0b' }} />
                        </div>
                        <div className="detail-content">
                            <div className="detail-label">Orario</div>
                            <div className="detail-value">
                                {formatTime(absence.start)} - {formatTime(absence.end)}
                                <span style={{ color: '#6b7280', marginLeft: '12px', fontSize: '13px' }}>
                                    ({calculateDuration()})
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Note */}
                    {absence.notes && (
                        <div className="detail-row" style={{ borderBottom: 'none' }}>
                            <div className="detail-icon" style={{ backgroundColor: '#f3e8ff' }}>
                                <FontAwesomeIcon icon={faFileAlt} style={{ color: '#9333ea' }} />
                            </div>
                            <div className="detail-content">
                                <div className="detail-label">Note</div>
                                <div className="notes-box">
                                    <p className="mb-0" style={{ color: '#374151', lineHeight: 1.6 }}>
                                        {absence.notes}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </ModalBody>

            <ModalFooter>
                <Button 
                    color="light" 
                    onClick={toggle}
                    style={{
                        borderRadius: '10px',
                        padding: '10px 20px',
                        fontWeight: 500
                    }}
                >
                    Chiudi
                </Button>
                <Button 
                    color="primary" 
                    onClick={handleEdit}
                    style={{
                        borderRadius: '10px',
                        padding: '10px 20px',
                        fontWeight: 500,
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        border: 'none'
                    }}
                >
                    <FontAwesomeIcon icon={faEdit} className="me-2" />
                    Modifica Assenza
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default AbsenceDetailsModal;
